import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { RoleService } from '../services/role.service';
import { UserRole } from '../shared/types/role.interface';

/**
 * Resolves `/`, `/bootstrap`, and any paths without exposing a bootstrap URL:
 * validates storage session, then redirects to role home or login.
 */
export const initialRedirectGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const roleService = inject(RoleService);

  const toLogin = () =>
    new RedirectCommand(router.createUrlTree(['/login']), { replaceUrl: true });

  const toDefaultRoute = () => {
    const role = auth.user()?.role as UserRole | undefined;
    const target = role != null ? roleService.getDefaultRoute(role) : '/dashboard';
    return new RedirectCommand(router.createUrlTree([target]), { replaceUrl: true });
  };

  if (typeof window === 'undefined') {
    return toLogin();
  }

  const user = auth.user();
  if (!user?.token) {
    return toLogin();
  }

  return auth.validateToken().pipe(
    map((response) => (response?.status ? toDefaultRoute() : toLogin())),
    catchError(() => of(toLogin()))
  );
};
